// «قبل الريّس»: a feature card with a photo, and two stacked cards. REFERENCE.md §7.
//
// Props: story (STORY or the CMS). Own copy: PROBLEMS in copy.js. Each card's link
// goes to its chapter in «يوم الريّس», `#day-<chapter>`.
//
// STORY.punch sits at the far side of the section head rather than closing the
// section: a closing line would add about 90px where the section has 10 left of
// its 5% (the eyebrow already takes 34).
//
// Scroll, desktop only: the photo drifts with the section (--ld-p), and each
// card's answer "solves" as it rises from 85% to 50% of the screen: its hairline
// draws and its check fills (--ld-solve). Everywhere else --ld-solve is unset, so
// the card reads complete, and phones get the short reveal in the CSS instead.

import { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import SectionHead from "./SectionHead";
import { PROBLEMS } from "./copy";
import { Photo } from "./media";
import { clamp01, DESKTOP, onScrollFrame, useMediaQuery, useMotionAllowed } from "./motion/env";
import useSectionProgress from "./motion/useSectionProgress";
import "./Problems.css";

// rooftop has a manufacturer's emblem in its top 15% (BRAND.md §7). Anchored low and
// zoomed, that part stays out of the frame on a phone and through the desktop drift.
const PHOTO_CROP = { "--ld-focal": "50% 100%", "--ld-zoom": 1.18 };

function useSolve(rootRef) {
    const wide = useMediaQuery(DESKTOP);
    const motion = useMotionAllowed();
    useEffect(() => {
        const root = rootRef.current;
        if (!root || !wide || !motion) return undefined;
        const answers = Array.from(root.querySelectorAll(".ld-problems__answer"));
        const written = new Map();
        const measure = () => {
            const vh = window.innerHeight || 1;
            answers.forEach((el) => {
                const solve = clamp01((0.85 * vh - el.getBoundingClientRect().top) / (0.35 * vh));
                if (written.get(el) === solve) return;
                written.set(el, solve);
                el.style.setProperty("--ld-solve", solve.toFixed(4));
            });
        };
        measure();
        const off = onScrollFrame(measure);
        return () => {
            off();
            answers.forEach((el) => el.style.removeProperty("--ld-solve"));
        };
    }, [rootRef, wide, motion]);
}

function TileContent({ item }) {
    const Icon = item.icon;
    return (
        <>
            <span className="ld-disc" aria-hidden="true"><Icon /></span>
            <div className="ld-problems__text">
                <h3 className="ld-h3">{item.title}</h3>
                <p className="ld-problems__line">{item.line}</p>
                <div className="ld-problems__answer">
                    <p className="ld-problems__label ld-label">
                        <span className="ld-problems__check" aria-hidden="true"><Check /></span>
                        {PROBLEMS.answerLabel}
                    </p>
                    <p className="ld-problems__fix">{item.fix}</p>
                    <a className="ld-link ld-problems__link" href={`#day-${item.chapter}`}>{item.link}</a>
                </div>
            </div>
        </>
    );
}

export default function Problems({ story }) {
    const root = useRef(null);
    useSectionProgress(root);
    useSolve(root);

    const [feature, ...side] = PROBLEMS.items;
    const punch = story?.punch ? <p className="ld-problems__punch">{story.punch}</p> : null;

    return (
        <section
            className="ld-section ld-section--day ld-section--after-trust ld-problems"
            id="problems"
            ref={root}
            aria-labelledby="ld-problems-title"
        >
            <div className="ld-decor" aria-hidden="true">
                <div className="ld-gridlines" style={{ "--ld-grid-at": "12% 18%" }} />
            </div>
            <div className="ld-container">
                <SectionHead id="ld-problems-title" eyebrow={story?.eyebrow} title={PROBLEMS.title} aside={punch} />
                <div className="ld-problems__grid">
                    <article
                        className="ld-problems__tile ld-problems__tile--feature ld-glass-day ld-glass-day--strong"
                        data-ld="problem"
                        style={{ "--ld-tile": 0 }}
                    >
                        <div className="ld-problems__media" data-ld="clip" style={{ "--ld-d": ".15s" }}>
                            <Photo
                                name="rooftop"
                                portrait={false}
                                sizes="(min-width: 1024px) 47vw, 118vw"
                                style={PHOTO_CROP}
                            />
                        </div>
                        <div className="ld-problems__body">
                            <TileContent item={feature} />
                        </div>
                    </article>
                    {side.map((item, i) => (
                        <article
                            key={item.id}
                            className="ld-problems__tile ld-glass-day ld-glass-day--strong"
                            data-ld="problem"
                            style={{ "--ld-tile": i + 1 }}
                        >
                            <TileContent item={item} />
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
